import { useMemo } from 'react';
import type { ObjectMetadata } from '../../../data/client';
import { useInboundRelations } from '../../../data/hooks';
import { GroupedForm, Skeleton } from '../../../ui';
import { RelatedGroup } from './RelatedGroup';
import { relatedSections } from './sectionModel';

/**
 * THE RECORD'S WORLD (D1-D6): every object that points at this record, as its own
 * collapsible section.
 *
 * The set is DERIVED from the metadata graph and answered by the server (D2), so an
 * object somebody creates tomorrow with a relation to this one gets a section here
 * without a code change - and nobody has to configure which sections exist.
 *
 * NOTHING QUERIES UNTIL ITS SECTION IS OPEN. Eight sections on one agency page would
 * otherwise be sixteen statements on mount, half of them unbounded counts (open risk
 * 3); each panel owns its own `enabled` and pays only when somebody looks.
 */
export interface RelatedSectionsProps {
  readonly object: ObjectMetadata;
  readonly recordId: string;
  /** D4: an archived parent gets no create and no link actions. */
  readonly readOnly: boolean;
}

export function RelatedSections({ object, recordId, readOnly }: RelatedSectionsProps) {
  const relations = useInboundRelations(object.id);
  const sections = useMemo(
    () => (relations.data === undefined ? [] : relatedSections(relations.data)),
    [relations.data],
  );

  if (relations.isPending) {
    return (
      <GroupedForm title="Related records">
        <div className="record-row record-row-stacked" aria-busy="true">
          <Skeleton className="h-6 w-40" label="Loading the related records" />
          <Skeleton className="h-6 w-32" />
        </div>
      </GroupedForm>
    );
  }

  /**
   * A LOAD FAILURE IS SAID OUT LOUD. The alternative - rendering nothing - is
   * indistinguishable from "this record is connected to nothing", which is the one
   * conclusion a reader must not draw from a network error.
   */
  if (relations.isError) {
    return (
      <GroupedForm title="Related records">
        <p role="alert" className="record-row record-row-stacked text-sm text-danger">
          The related records could not be loaded, so this list may be incomplete. Reload the page
          to try again.
        </p>
      </GroupedForm>
    );
  }

  /**
   * NO SECTIONS IS A REAL STATE and it is not an error: an object nothing points at - a
   * standalone list, a lookup table - simply has no world around it.
   *
   * One muted LINE rather than nothing at all, and rather than an `EmptyState` block.
   * Nothing at all would leave the phone's Related tab blank, which reads as a screen
   * that failed to load; a block would be a full panel of apology on every page of that
   * object. D25's reasoning, applied to the whole panel instead of to one section.
   */
  if (sections.length === 0) {
    return (
      /* BR-RECORD: the same muted line the empty task and related lists use (DESIGN.md H4). */
      <p className="record-empty-line">
        {`No related records. Records from other objects appear here as soon as one of them links to this ${object.labelSingular.toLowerCase()}.`}
      </p>
    );
  }

  /*
    WAVE 3 (R-B1): EACH RELATED OBJECT IS ITS OWN GROUP BOX, side by side with the field
    sections, and there is no "Related" pane title over the set.
    `spec/screens/record.md` section 2 item 6 says "each related object as ONE grouped list
    section", and a title above a stack of titled boxes is the second frame the owner counted
    (owner-37, owner-38): the boxes were sections of a section, separated by a `divide-y` the
    kit's group box does not have. Every section now brings its own kit title and hairlines,
    which is also what makes them layout-agnostic - a group renders in any column.
  */
  return (
    <>
      {sections.map((section) => (
        <RelatedGroup
          key={section.id}
          parent={object}
          recordId={recordId}
          section={section}
          readOnly={readOnly}
        />
      ))}
    </>
  );
}
